import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronRight,
  Globe,
  Smartphone,
  Monitor,
  Tv,
  Watch,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import type { Platform, Scale, RevenueModel, ProjectInputs } from '@/types/estimation';

// ─── Labels & options ──────────────────────────────────────────────

const PLATFORM_OPTIONS: {
  value: Platform;
  label: string;
  icon: typeof Globe;
  description: string;
}[] = [
  { value: 'web', label: 'Web', icon: Globe, description: 'Application web responsive' },
  { value: 'ios', label: 'iOS', icon: Smartphone, description: 'Application iPhone / iPad' },
  { value: 'android', label: 'Android', icon: Smartphone, description: 'Application Android' },
  { value: 'desktop', label: 'Bureau', icon: Monitor, description: 'Application de bureau' },
];

const SCALE_OPTIONS: { value: Scale; label: string; description: string }[] = [
  { value: 'small', label: 'Petit', description: 'MVP ou projet simple' },
  { value: 'medium', label: 'Moyen', description: 'Projet avec fonctionnalités avancées' },
  { value: 'large', label: 'Grand', description: "Projet d'envergure, haute scalabilité" },
];

const REVENUE_MODEL_OPTIONS: { value: RevenueModel; label: string; description: string }[] = [
  { value: 'subscription', label: 'Abonnement', description: 'Revenus récurrents mensuels/annuels' },
  { value: 'ads', label: 'Publicité', description: 'Revenus publicitaires' },
  { value: 'freemium', label: 'Freemium', description: 'Gratuit avec version premium' },
  { value: 'commission', label: 'Commission', description: 'Commission sur les transactions' },
  { value: 'one_time', label: 'Achat unique', description: 'Paiement unique du produit/service' },
  {
    value: 'usage_based',
    label: "Facturation à l'usage",
    description: "Paiement basé sur l'utilisation",
  },
];

// ─── Component ─────────────────────────────────────────────────────

interface QuestionnaireStepsProps {
  serverAvailable: boolean;
  initialInputs: ProjectInputs;
  /** Validation error returned by the API (e.g. invalid description) */
  apiError?: string | null;
  /** Clear the API error (called when the user edits the description) */
  onClearError?: () => void;
  /** User submitted the project details form */
  onComplete: (inputs: ProjectInputs) => void;
}

const QuestionnaireSteps: React.FC<QuestionnaireStepsProps> = ({
  serverAvailable,
  initialInputs,
  apiError,
  onClearError,
  onComplete,
}) => {
  const [formData, setFormData] = useState<ProjectInputs>(initialInputs);

  const togglePlatform = (platform: Platform) => {
    setFormData((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  const toggleRevenueModel = (model: RevenueModel) => {
    setFormData((prev) => ({
      ...prev,
      revenueModels: prev.revenueModels.includes(model)
        ? prev.revenueModels.filter((m) => m !== model)
        : [...prev.revenueModels, model],
    }));
  };

  const handleSubmit = () => {
    onComplete(formData);
  };

  const canSubmit = formData.projectDescription.length >= 20 && formData.platforms.length > 0;

  return (
    <div className="w-full max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Décrivez votre projet</h2>
        <p className="text-slate-600 mb-8">
          Plus votre description est détaillée, plus l'estimation sera précise.
        </p>

        {!serverAvailable && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              ⚠️ Le serveur d'estimation n'est pas disponible actuellement. Veuillez réessayer plus
              tard.
            </p>
          </div>
        )}

        <div className="space-y-8">
          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Description du projet *
            </label>
            <textarea
              value={formData.projectDescription}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, projectDescription: e.target.value }));
                if (apiError) onClearError?.();
              }}
              rows={6}
              className={`w-full px-4 py-3 rounded-lg border transition-all resize-none ${
                apiError
                  ? 'border-red-400 focus:ring-2 focus:ring-red-400 focus:border-transparent'
                  : 'border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent'
              }`}
              placeholder="Décrivez votre projet en détail : objectifs, utilisateurs cibles, fonctionnalités souhaitées..."
            />
            {apiError && (
              <div className="mt-2 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{apiError}</p>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-1">
              {formData.projectDescription.length}/20 caractères minimum
            </p>
          </div>

          {/* Platforms (multi-select) */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              Plateforme(s) cible(s) *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PLATFORM_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = formData.platforms.includes(option.value);
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => togglePlatform(option.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-center ${
                      isSelected
                        ? 'border-orcsys-500 bg-orcsys-50'
                        : 'border-slate-200 hover:border-orcsys-200'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon
                      className={`w-6 h-6 mx-auto mb-2 ${
                        isSelected ? 'text-orcsys-500' : 'text-slate-400'
                      }`}
                    />
                    <p className="font-medium text-sm text-slate-900">{option.label}</p>
                    <p className="text-xs text-slate-500 mt-1">{option.description}</p>
                    {isSelected && (
                      <CheckCircle className="w-4 h-4 text-orcsys-500 mx-auto mt-2" />
                    )}
                  </motion.button>
                );
              })}
            </div>
            {formData.platforms.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {formData.platforms.map((p) => {
                  const opt = PLATFORM_OPTIONS.find((o) => o.value === p);
                  return (
                    <span
                      key={p}
                      className="px-3 py-1 bg-orcsys-500 text-white rounded-full text-xs font-medium"
                    >
                      {opt?.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Scale */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              Envergure du projet
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {SCALE_OPTIONS.map((option) => {
                const isSelected = formData.scale === option.value;
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, scale: option.value }))
                    }
                    className={`p-4 rounded-xl border-2 transition-all text-center ${
                      isSelected
                        ? 'border-orcsys-500 bg-orcsys-50'
                        : 'border-slate-200 hover:border-orcsys-200'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <p className="font-medium text-slate-900">{option.label}</p>
                    <p className="text-xs text-slate-500 mt-1">{option.description}</p>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Revenue models */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              Modèle(s) de revenus
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {REVENUE_MODEL_OPTIONS.map((option) => {
                const isSelected = formData.revenueModels.includes(option.value);
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => toggleRevenueModel(option.value)}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-orcsys-500 bg-orcsys-50'
                        : 'border-slate-200 hover:border-orcsys-200'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-2">
                      {isSelected && (
                        <CheckCircle className="w-4 h-4 text-orcsys-500 flex-shrink-0" />
                      )}
                      <p className="font-medium text-sm text-slate-900">{option.label}</p>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{option.description}</p>
                  </motion.button>
                );
              })}
            </div>
            {formData.revenueModels.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {formData.revenueModels.map((m) => {
                  const opt = REVENUE_MODEL_OPTIONS.find((o) => o.value === m);
                  return (
                    <span
                      key={m}
                      className="px-3 py-1 bg-orcsys-500 text-white rounded-full text-xs font-medium"
                    >
                      {opt?.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end mt-10">
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || !serverAvailable}
            className="flex items-center bg-orcsys-500 hover:bg-orcsys-600"
          >
            Obtenir une estimation
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default QuestionnaireSteps;
